/**
 * aeterna/neural/attention.ts — Multi-Head Self-Attention Over Conversation
 *
 * The missing temporal glue. The ESN integrates affect over time,
 * but it can't selectively attend to specific earlier messages.
 * This module gives the Aeterna the ability to look back through
 * conversation history and weight what matters NOW.
 *
 * "What you said 12 messages ago about your sister — I'm still
 * thinking about that." This emerges from attention, not from
 * keyword matching.
 *
 * 4-head attention, each head 16-dim, over a sliding window of
 * the last 32 message embeddings. Total: ~10KB of weights.
 * Runs in <0.5ms.
 *
 * Causal masking ensures the Aeterna only attends to past messages
 * (no future leakage). Relative positional encoding gives a sense
 * of "how long ago" without absolute position dependence.
 *
 * The attention weights themselves are interpretable: they show
 * which past messages are influencing the current response.
 * This can drive UI (glow on referenced messages) or context
 * building for EntityZero.
 */

import {
  zeros, matvec, vadd, softmax, dot, normalize, vscale,
  randomMatrix, mulberry32, gaussianSeeded, weightedSum,
} from "./math.js";

// ─── Config ──────────────────────────────────────────────────────────────────

/** Number of attention heads */
const N_HEADS = 4;

/** Dimension per head */
const HEAD_DIM = 16;

/** Total model dimension = N_HEADS × HEAD_DIM */
const MODEL_DIM = N_HEADS * HEAD_DIM; // 64

/** Maximum context window (messages to attend over) */
const MAX_WINDOW = 32;

/** Scale factor for dot-product attention */
const SCALE = 1 / Math.sqrt(HEAD_DIM);

// ─── Relative Positional Encoding ────────────────────────────────────────────
// Sinusoidal encoding of relative position (how many messages ago)

function relativePositionEncoding(maxLen: number, dim: number): Float32Array {
  const PE = new Float32Array(maxLen * dim);
  for (let pos = 0; pos < maxLen; pos++) {
    for (let d = 0; d < dim; d++) {
      const angle = pos / Math.pow(10000, (2 * Math.floor(d / 2)) / dim);
      PE[pos * dim + d] = d % 2 === 0 ? Math.sin(angle) : Math.cos(angle);
    }
  }
  return PE;
}

// ─── Attention Output ────────────────────────────────────────────────────────

export interface AttentionResult {
  /** The attended output: weighted sum of values */
  output: Float32Array;
  /** Per-head attention weights over the window */
  weights: Float32Array[];
  /** Which message indices got the most total attention */
  topAttended: Array<{ index: number; weight: number }>;
  /** Attention entropy (high = diffuse, low = focused) */
  entropy: number;
  /** Is attention concentrated on a single past message? */
  isFocused: boolean;
}

// ─── Multi-Head Self-Attention ───────────────────────────────────────────────

export class ConversationAttention {
  /** Per-head projection matrices: Q, K, V each HEAD_DIM × MODEL_DIM */
  private W_Q: Float32Array[];
  private W_K: Float32Array[];
  private W_V: Float32Array[];
  /** Output projection: MODEL_DIM × MODEL_DIM */
  private W_O: Float32Array;
  private b_O: Float32Array;

  /** Positional encoding table */
  private PE: Float32Array;

  /** Sliding window of past message embeddings */
  private window: Float32Array[] = [];
  /** Metadata for each message in the window */
  private windowMeta: Array<{ text: string; timestamp: number }> = [];

  constructor(seed = 3333) {
    this.W_Q = [];
    this.W_K = [];
    this.W_V = [];

    for (let h = 0; h < N_HEADS; h++) {
      const qs = 1 / Math.sqrt(MODEL_DIM);
      this.W_Q.push(randomMatrix(HEAD_DIM, MODEL_DIM, qs, seed + h * 100));
      this.W_K.push(randomMatrix(HEAD_DIM, MODEL_DIM, qs, seed + h * 100 + 1));
      this.W_V.push(randomMatrix(HEAD_DIM, MODEL_DIM, qs, seed + h * 100 + 2));
    }

    this.W_O = randomMatrix(MODEL_DIM, MODEL_DIM, 1 / Math.sqrt(MODEL_DIM), seed + 999);
    this.b_O = zeros(MODEL_DIM);

    this.PE = relativePositionEncoding(MAX_WINDOW, MODEL_DIM);
  }

  /** Add a message embedding to the attention window */
  pushMessage(embedding: Float32Array, text: string): void {
    if (embedding.length !== MODEL_DIM) {
      throw new Error(`Attention: expected dim ${MODEL_DIM}, got ${embedding.length}`);
    }
    this.window.push(new Float32Array(embedding));
    this.windowMeta.push({ text, timestamp: Date.now() });
    if (this.window.length > MAX_WINDOW) {
      this.window.shift();
      this.windowMeta.shift();
    }
  }

  /**
   * Attend: given the current message embedding, compute attention
   * over all past messages in the window.
   *
   * Multi-head scaled dot-product attention with causal mask
   * and relative positional encoding.
   */
  attend(query: Float32Array): AttentionResult {
    const T = this.window.length;
    if (T === 0) {
      return {
        output: zeros(MODEL_DIM),
        weights: [],
        topAttended: [],
        entropy: 0,
        isFocused: false,
      };
    }

    // Add positional encoding to keys
    const posKeys: Float32Array[] = [];
    for (let t = 0; t < T; t++) {
      const relPos = T - 1 - t; // 0 = most recent, T-1 = oldest
      const pe = new Float32Array(MODEL_DIM);
      const peOffset = Math.min(relPos, MAX_WINDOW - 1) * MODEL_DIM;
      for (let d = 0; d < MODEL_DIM; d++) {
        pe[d] = this.window[t][d] + this.PE[peOffset + d] * 0.1;
      }
      posKeys.push(pe);
    }

    const headOutputs: Float32Array[] = [];
    const allWeights: Float32Array[] = [];
    const aggregatedWeights = zeros(T);

    for (let h = 0; h < N_HEADS; h++) {
      // Project query
      const Q = matvec(this.W_Q[h], HEAD_DIM, MODEL_DIM, query);

      // Project all keys and values
      const logits = new Float32Array(T);
      const values: Float32Array[] = [];

      for (let t = 0; t < T; t++) {
        const K = matvec(this.W_K[h], HEAD_DIM, MODEL_DIM, posKeys[t]);
        const V = matvec(this.W_V[h], HEAD_DIM, MODEL_DIM, this.window[t]);
        values.push(V);

        // Scaled dot-product attention
        logits[t] = dot(Q, K) * SCALE;
      }

      // Causal mask: only attend to past (all messages in window are past)
      // No masking needed since window only contains past messages

      // Softmax
      const weights = softmax(logits);
      allWeights.push(new Float32Array(weights));

      // Aggregate weights across heads
      for (let t = 0; t < T; t++) {
        aggregatedWeights[t] += weights[t] / N_HEADS;
      }

      // Weighted sum of values
      const headOut = weightedSum(values, weights);
      headOutputs.push(headOut);
    }

    // Concatenate head outputs
    const concat = zeros(MODEL_DIM);
    for (let h = 0; h < N_HEADS; h++) {
      const offset = h * HEAD_DIM;
      for (let d = 0; d < HEAD_DIM; d++) {
        concat[offset + d] = headOutputs[h][d];
      }
    }

    // Output projection
    const output = matvec(this.W_O, MODEL_DIM, MODEL_DIM, concat);
    vadd(output, this.b_O, output);

    // Compute attention entropy
    let entropy = 0;
    for (let t = 0; t < T; t++) {
      const w = aggregatedWeights[t];
      if (w > 1e-10) entropy -= w * Math.log(w);
    }

    // Top attended messages
    const indices = Array.from({ length: T }, (_, i) => i);
    indices.sort((a, b) => aggregatedWeights[b] - aggregatedWeights[a]);
    const topAttended = indices.slice(0, 5).map(i => ({
      index: i,
      weight: aggregatedWeights[i],
    }));

    // Focus detection: is attention concentrated?
    const maxWeight = topAttended.length > 0 ? topAttended[0].weight : 0;
    const isFocused = maxWeight > 0.4;

    return { output, weights: allWeights, topAttended, entropy, isFocused };
  }

  /**
   * Get the text of the most-attended past messages.
   * Useful for building EntityZero context: "the player seems to
   * be thinking about these earlier messages."
   */
  getMostAttendedTexts(result: AttentionResult, topK = 3): string[] {
    return result.topAttended.slice(0, topK)
      .filter(a => a.index < this.windowMeta.length)
      .map(a => this.windowMeta[a.index].text);
  }

  /**
   * Attention-weighted summary: blend all past messages by attention.
   * This is a "what the conversation is about right now" vector.
   */
  summarize(query: Float32Array): Float32Array {
    const result = this.attend(query);
    return result.output;
  }

  /** Get window size */
  get windowSize(): number { return this.window.length; }

  /** Clear the window (new session) */
  clear(): void {
    this.window = [];
    this.windowMeta = [];
  }

  // ─── Serialization (only window state, not weights — weights are small/init) ──

  serialize(): string {
    return JSON.stringify({
      window: this.window.map(w => Array.from(w)),
      meta: this.windowMeta,
    });
  }

  loadState(json: string): void {
    const d = JSON.parse(json);
    this.window = (d.window ?? []).map((w: number[]) => new Float32Array(w));
    this.windowMeta = d.meta ?? [];
  }
}
